import React, { useState } from 'react';
import {
  Table, Tag, Button, Space, Typography, Alert, Modal, Radio,
  Upload, Input, Form, Select, Tooltip, message
} from 'antd';
import {
  PlusOutlined, SearchOutlined, ReloadOutlined,
  UploadOutlined, InfoCircleOutlined
} from '@ant-design/icons';

const { Text } = Typography;

// ══════════════════════════════════════════════
//  Mock 数据
// ══════════════════════════════════════════════
// TODO: 联调时替换为接口数据
const MOCK_FINALIZED_FW = [
  { id: 'fw_001', name: '主控固件', version: 'V3.0.0', productId: 'P5xJS90pll' },
  { id: 'fw_002', name: 'WiFi模组固件', version: 'V2.1.0', productId: 'P5xJS90pll' },
  { id: 'fw_003', name: 'MCU固件', version: 'V2.0.0', productId: 'P5xJS90pll' },
  { id: 'fw_004', name: '传感器固件', version: 'V3.0.1', productId: 'P5xJS90pll' },
];

const OTA_STATUS = {
  upgrading: { text: '升级中', color: 'processing' },
  success: { text: '成功', color: 'success' },
  fail: { text: '失败', color: 'error' },
};

// TODO: 联调时替换为接口数据
const MOCK_OTA_RECORDS = [
  { key: '1', time: '2026-06-17 14:30:00', source: 'finalized', fwName: '主控固件', targetVersion: 'V3.0.0', status: 'success', deviceVersion: 'V3.0.0', fwId: 'fw_001' },
  { key: '2', time: '2026-06-16 09:15:00', source: 'direct', targetVersion: 'test-v2.9.1', status: 'upgrading', deviceVersion: null, fwId: null, fileName: 'hotfix_v2.9.1.bin' },
  { key: '3', time: '2026-06-15 16:45:00', source: 'finalized', fwName: 'MCU固件', targetVersion: 'V2.0.0', status: 'fail', deviceVersion: null, fwId: 'fw_003' },
  { key: '4', time: '2026-06-14 11:20:00', source: 'direct', targetVersion: 'test-v2.8.5', status: 'success', deviceVersion: 'V2.8.5', fwId: null, fileName: 'patch_v2.8.5.bin' },
  { key: '5', time: '2026-06-13 08:00:00', source: 'finalized', fwName: 'WiFi模组固件', targetVersion: 'V2.1.0', status: 'success', deviceVersion: 'V2.1.0', fwId: 'fw_002' },
];

// ══════════════════════════════════════════════
//  固件OTA Tab 主组件
// ══════════════════════════════════════════════
const FirmwareOtaTab = ({ deviceLicense, currentFwVersion, productName, productId, isPlatform }) => {
  const [otaRecords, setOtaRecords] = useState(MOCK_OTA_RECORDS);
  const [searchText, setSearchText] = useState('');
  const [modalVisible, setModalVisible] = useState(false);
  const [otaMode, setOtaMode] = useState('finalized');
  const [selectedFw, setSelectedFw] = useState(undefined);
  const [targetVersion, setTargetVersion] = useState('');
  const [uploadedFile, setUploadedFile] = useState(null);
  const [riskModalVisible, setRiskModalVisible] = useState(false);
  const [, setPendingSubmit] = useState(null);
  const [otaForm] = Form.useForm();

  // 过滤已定版固件：仅展示当前产品下的已定版升级固件
  const finalizedFwOptions = MOCK_FINALIZED_FW
    .filter(fw => fw.productId === productId)
    .map(fw => ({ value: fw.id, label: `${fw.name} / ${fw.version}`, fw }));

  // 搜索过滤
  const filteredRecords = otaRecords.filter(r => {
    if (!searchText) return true;
    const s = searchText.toLowerCase();
    return (
      (r.targetVersion && r.targetVersion.toLowerCase().includes(s)) ||
      (r.fwName && r.fwName.toLowerCase().includes(s)) ||
      (r.deviceVersion && r.deviceVersion.toLowerCase().includes(s)) ||
      (r.time && r.time.toLowerCase().includes(s))
    );
  });

  // 再次下发
  const handleRedispatch = (record) => {
    const newRecord = {
      ...record,
      key: String(Date.now()),
      time: new Date().toLocaleString('zh-CN', { hour12: false }),
      status: 'upgrading',
      deviceVersion: null,
    };
    setOtaRecords(prev => [newRecord, ...prev]);
    message.success('已重新发起固件升级');
  };

  // 发起OTA - 打开弹窗
  const handleOpenModal = () => {
    setOtaMode('finalized');
    setSelectedFw(undefined);
    setTargetVersion('');
    setUploadedFile(null);
    otaForm.resetFields();
    setModalVisible(true);
  };

  // 模式切换
  const handleModeChange = (e) => {
    setOtaMode(e.target.value);
    setSelectedFw(undefined);
    setTargetVersion('');
    setUploadedFile(null);
    otaForm.resetFields();
  };

  // 固件选择变化
  const handleFwChange = (value) => {
    setSelectedFw(value);
    const fw = MOCK_FINALIZED_FW.find(f => f.id === value);
    if (fw) {
      setTargetVersion(fw.version);
    }
  };

  // 提交发起OTA
  const handleSubmit = () => {
    if (otaMode === 'finalized') {
      if (!selectedFw) {
        message.warning('请选择目标固件');
        return;
      }
      doSubmit();
    } else {
      if (!uploadedFile) {
        message.warning('请上传固件包');
        return;
      }
      if (!targetVersion.trim()) {
        message.warning('请输入目标版本号');
        return;
      }
      // 直接上传模式：先弹出风险确认
      setPendingSubmit({ type: 'direct' });
      setRiskModalVisible(true);
    }
  };

  const doSubmit = () => {
    const now = new Date().toLocaleString('zh-CN', { hour12: false });
    let newRecord;

    if (otaMode === 'finalized') {
      const fw = MOCK_FINALIZED_FW.find(f => f.id === selectedFw);
      newRecord = {
        key: String(Date.now()),
        time: now,
        source: 'finalized',
        fwName: fw?.name || '',
        targetVersion: targetVersion,
        status: 'upgrading',
        deviceVersion: null,
        fwId: selectedFw,
      };
    } else {
      newRecord = {
        key: String(Date.now()),
        time: now,
        source: 'direct',
        targetVersion: targetVersion.trim(),
        status: 'upgrading',
        deviceVersion: null,
        fwId: null,
        fileName: uploadedFile?.name || '',
      };
    }

    setOtaRecords(prev => [newRecord, ...prev]);
    setModalVisible(false);
    setRiskModalVisible(false);
    setPendingSubmit(null);
    message.success('已发起固件升级');
  };

  // 表格列定义
  const columns = [
    {
      title: '升级时间',
      dataIndex: 'time',
      key: 'time',
      width: 170,
    },
    {
      title: '固件来源',
      dataIndex: 'source',
      key: 'source',
      width: 120,
      render: (v, r) => v === 'finalized'
        ? <Tag color="blue" style={{ border: 'none' }}>已定版固件</Tag>
        : <Tag color="orange" style={{ border: 'none' }}>直接上传</Tag>,
    },
    {
      title: '固件信息',
      key: 'fwInfo',
      width: 160,
      render: (_, r) => {
        if (r.source === 'finalized') {
          return (
            <div>
              <div style={{ fontWeight: 500 }}>{r.fwName || '—'}</div>
              <Text type="secondary" style={{ fontSize: 12 }}>{r.targetVersion}</Text>
            </div>
          );
        }
        return (
          <div>
            <div style={{ fontWeight: 500 }}>直接上传</div>
            <Text type="secondary" style={{ fontSize: 12 }}>{r.targetVersion}</Text>
          </div>
        );
      },
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 90,
      render: (v) => {
        const s = OTA_STATUS[v];
        return s ? <Tag color={s.color} style={{ border: 'none' }}>{s.text}</Tag> : '—';
      },
    },
    {
      title: '设备上报版本',
      key: 'deviceVersion',
      width: 160,
      render: (_, r) => {
        if (r.status !== 'success' || !r.deviceVersion) return <Text type="secondary">—</Text>;
        const match = r.deviceVersion === r.targetVersion;
        return match
          ? <Text code>{r.deviceVersion}</Text>
          : (
            <Tooltip title="设备上报版本与目标版本不一致，可能存在升级风险">
              <Text style={{ color: '#ff4d4f', fontFamily: 'monospace' }}>{r.deviceVersion} <InfoCircleOutlined style={{ fontSize: 12 }} /></Text>
            </Tooltip>
          );
      },
    },
    {
      title: '操作',
      key: 'action',
      width: 100,
      render: (_, r) => {
        if (isPlatform) return <Text type="secondary">—</Text>;
        return r.status === 'upgrading'
          ? <Tooltip title="升级中不可再次下发"><Button type="link" size="small" disabled>再次下发</Button></Tooltip>
          : <Button type="link" size="small" onClick={() => handleRedispatch(r)}>再次下发</Button>;
      },
    },
  ];

  return (
    <div>
      {/* 提示区 */}
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16, borderRadius: 8 }}
        message="固件升级是实时下发，状态回传存在一定延迟"
      />

      {/* 设备摘要区 */}
      <div style={{
        marginBottom: 16,
        padding: '10px 16px',
        background: '#fafafa',
        borderRadius: 8,
        display: 'flex',
        alignItems: 'center',
        gap: 24,
        flexWrap: 'wrap'
      }}>
        <span><Text type="secondary">License：</Text>
          <Text copyable style={{ fontFamily: 'monospace' }}>{deviceLicense}</Text>
        </span>
        <span><Text type="secondary">当前固件版本：</Text>
          <Text strong>{currentFwVersion}</Text>
        </span>
        <span><Text type="secondary">产品：</Text>
          <Text>{productName}</Text>
          <Text type="secondary" style={{ marginLeft: 4 }}>产品ID：{productId}</Text>
        </span>
      </div>

      {/* 操作区 + 搜索 */}
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Space>
          <Input
            placeholder="搜索目标版本"
            prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
            allowClear
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 240 }}
          />
          <Button icon={<ReloadOutlined />} onClick={() => setSearchText('')}>重置</Button>
        </Space>
        {!isPlatform && (
          <Button type="primary" icon={<PlusOutlined />} onClick={handleOpenModal}>
            发起固件OTA
          </Button>
        )}
      </div>

      {/* OTA 升级记录表格 */}
      <Table
        columns={columns}
        dataSource={filteredRecords}
        rowKey="key"
        pagination={{
          showSizeChanger: true,
          showTotal: (t) => `共 ${t} 条记录`,
        }}
        locale={{ emptyText: searchText ? '未找到匹配的记录' : '暂无 OTA 升级记录' }}
        scroll={{ x: 900 }}
      />

      {/* ══════════════════════════════════════════════ */}
      {/*  发起固件OTA 弹窗 */}
      {/* ══════════════════════════════════════════════ */}
      <Modal
        title="发起固件OTA"
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={handleSubmit}
        okText="确认下发"
        cancelText="取消"
        width={600}
        destroyOnClose
      >
        {/* 设备信息提示 */}
        <div style={{
          marginBottom: 16,
          padding: '8px 12px',
          background: '#fafafa',
          borderRadius: 6,
          fontSize: 13,
        }}>
          <Text type="secondary">当前设备：</Text>
          <Text style={{ fontFamily: 'monospace' }}>{deviceLicense}</Text>
          <Text type="secondary" style={{ marginLeft: 8 }}>当前版本：</Text>
          <Text strong>{currentFwVersion}</Text>
        </div>

        {/* 模式切换 */}
        <Form layout="vertical" style={{ marginBottom: 16 }}>
          <Form.Item label={<Text strong>固件来源</Text>}>
            <Radio.Group value={otaMode} onChange={handleModeChange}>
              <Radio.Button value="finalized">选择已定版固件</Radio.Button>
              <Radio.Button value="direct">直接上传固件包</Radio.Button>
            </Radio.Group>
          </Form.Item>
        </Form>

        {/* 模式一：选择已定版固件 */}
        {otaMode === 'finalized' && (
          <div>
            <Form form={otaForm} layout="vertical">
              <Form.Item
                label={<Text strong>选择固件</Text>}
                required
              >
                <Select
                  placeholder="请选择目标固件"
                  value={selectedFw}
                  onChange={handleFwChange}
                  options={finalizedFwOptions}
                  notFoundContent={
                    <div style={{ padding: 16, textAlign: 'center', color: '#8c8c8c' }}>
                      <div>当前产品下暂无已定版的升级固件</div>
                      <div style={{ fontSize: 12, marginTop: 4 }}>您可以切换至「直接上传固件包」模式</div>
                    </div>
                  }
                  style={{ width: '100%' }}
                />
              </Form.Item>
              <Form.Item label={<Text strong>固件版本</Text>}>
                <Input value={targetVersion} disabled style={{ fontFamily: 'monospace' }} />
              </Form.Item>
            </Form>
            <Text type="secondary" style={{ fontSize: 12 }}>
              <InfoCircleOutlined style={{ marginRight: 4 }} />
              下发后设备将生效固件级物模型配置
            </Text>
          </div>
        )}

        {/* 模式二：直接上传固件包 */}
        {otaMode === 'direct' && (
          <div>
            <Form layout="vertical">
              <Form.Item
                label={<Text strong>上传固件包</Text>}
                required
                extra="仅支持 .bin 格式固件文件"
              >
                <Upload
                  maxCount={1}
                  accept=".bin"
                  beforeUpload={(file) => {
                    setUploadedFile(file);
                    return false;
                  }}
                  onRemove={() => setUploadedFile(null)}
                  fileList={uploadedFile ? [uploadedFile] : []}
                >
                  <Button icon={<UploadOutlined />}>选择固件文件</Button>
                </Upload>
              </Form.Item>
              <Form.Item
                label={<Text strong>目标版本号</Text>}
                required
              >
                <Input
                  placeholder="请输入目标版本号，例如：V3.1.0"
                  value={targetVersion}
                  onChange={(e) => setTargetVersion(e.target.value)}
                />
              </Form.Item>
            </Form>
            <Text type="secondary" style={{ fontSize: 12 }}>
              <InfoCircleOutlined style={{ marginRight: 4 }} />
              下发后设备将生效产品级物模型配置
            </Text>
          </div>
        )}
      </Modal>

      {/* ══════════════════════════════════════════════ */}
      {/*  直接上传风险确认弹窗 */}
      {/* ══════════════════════════════════════════════ */}
      <Modal
        title="风险提示"
        open={riskModalVisible}
        onCancel={() => {
          setRiskModalVisible(false);
          setPendingSubmit(null);
        }}
        onOk={doSubmit}
        okText="确认下发"
        cancelText="取消"
        okButtonProps={{ danger: true }}
        width={480}
      >
        <div style={{ padding: '8px 0' }}>
          <Text>此固件未经平台测试验证，直接下发可能导致设备异常。确定要继续吗？</Text>
        </div>
      </Modal>
    </div>
  );
};

export default FirmwareOtaTab;
